import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Button, Dropdown, Menu, Select, Tag } from 'antd'
import SmartTable, { SmartTableDataTypes } from '../../Components/shared/SmartTable'
import StudentInfo from '../../Components/shared/StudentInfo'
import AdminResetPassword from '../../Components/shared/AdminResetPassword'
import PublipostageDrawer from '../../Components/institution/PublipostageDrawer'
import { InstitutionOptions, InstitutionRoles, OtherRoles } from '../../utils/constants'
import Analytics from '../../utils/analytics'
import { getUser } from '../../reducers/UserReducer'
import { getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { faFilter, faKey, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import { faFileArchive } from '@fortawesome/pro-regular-svg-icons'
import { fetchInstitutionTemplates } from '../../reducers/InstitutionTemplatesReducer/InstitutionTemplatesReducer'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import { bindActionCreators } from 'redux'
import { getStatus, StudentStatusLabels, StudentStatusTypes } from '../../utils/entities/student'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ComponentWithIconLabel from '../../Components/icons/ComponentWithIconLabel'
import { isString } from 'lodash'
import { StatusTagStyles } from '../school/StudentsPage/StudentsListView/StudentsTable'

const STUDENT_INFO = 'STUDENT_INFO'
const RESET_PASSWORD = 'RESET_PASSWORD'

const mapStateToProps = state => ({
  t: getTranslate(state.locale),
  user: getUser(state.getUser)
})

const mapDispatchToProps = dispatch => ({
  fetchInstitutionTemplates: bindActionCreators(fetchInstitutionTemplates, dispatch)
})

const StudentsView = ({ user, fetchInstitutionTemplates, t }) => {
  const { institution, institutionStudents, optionsMap } = useContext(InstitutionContext)

  const [selectedStudent, setSelectedStudent] = useState({ data: null, type: null })
  const [selectedStatuses, setSelectedStatuses] = useState([StudentStatusTypes.CURRENTLY_STUDYING])
  const usePublipostage = optionsMap[InstitutionOptions.PUBLIPOSTAGE]

  useEffect(() => Analytics.pageView('/institution-students'), [])
  useEffect(() => {
    if (usePublipostage && institution) {
      fetchInstitutionTemplates(institution.id, user)
    }
  }, [usePublipostage, user?.id, institution?.id, fetchInstitutionTemplates])

  const canResetPassword = useMemo(() => [InstitutionRoles.HOSPITAL_ADMIN, OtherRoles.ADMIN].includes(user.roles[0]), [user.roles])
  const showNiss = useMemo(() => [InstitutionRoles.HOSPITAL_ADMIN].includes(user.roles[0]), [user.roles])
  const columns = useMemo(() => {
    let columns = [
      { type: SmartTableDataTypes.ID, key: 'id' },
      { type: SmartTableDataTypes.STRING, name: t('common.section'), key: 'section' }
    ]

    columns = [
      ...columns,
      { type: SmartTableDataTypes.STRING, name: t('Lastname'), key: 'lastname' },
      { type: SmartTableDataTypes.STRING, name: t('Firstname'), key: 'firstname' },
      { type: SmartTableDataTypes.STRING, name: t('Email'), key: 'email' },
      { type: SmartTableDataTypes.STRING, name: t('Registration number'), key: 'matricule' },
      { type: SmartTableDataTypes.STRING, name: t('Phone'), key: 'phoneNumber' },
      { type: SmartTableDataTypes.DATE, name: t('Birthdate'), key: 'birthdate', format: 'DD/MM/YYYY' },
      { type: SmartTableDataTypes.STRING, name: t('Gender'), key: 'gender' },
      { type: SmartTableDataTypes.STRING, name: t('Nationality'), key: 'nationality' },
      {
        type: SmartTableDataTypes.STRING,
        name: t('common.status'),
        key: 'status',
        readonlyRender: value => {
          let body = null

          if (isString(value)) {
            body = <Tag style={StatusTagStyles[value === null ? 'DEFAULT' : value]}> {t('student_status_codes.' + value.toLowerCase())} </Tag>
          }

          return body
        }
      },
      {
        type: SmartTableDataTypes.BOOLEAN_TAG,
        name: t('Data use'),
        key: 'dataUsage',
        yesMessage: 'Accepts the use of their data for recruitment purposes',
        noMessage: 'Does not accept the use of their data for recruitment purposes'
      }
    ]

    if (showNiss) {
      columns.push({ type: SmartTableDataTypes.STRING, name: t('NISS'), key: 'niss', password: true })
    }

    return columns
  }, [showNiss, t])

  const additionalActions = useMemo(() => {
    const additionalActions = [
      {
        key: 'student-info',
        iconName: faUserGraduate,
        type: 'primary',
        title: t('Student sheet'),
        onClick: s => setSelectedStudent({ data: s, type: STUDENT_INFO })
      }
    ]

    if (usePublipostage) {
      additionalActions.push({
        key: 'publipostage',
        iconName: faFileArchive,
        type: 'primary',
        title: t('Publipostage templates list'),
        onClick: s => setSelectedStudent({ data: s, type: InstitutionOptions.PUBLIPOSTAGE })
      })
    }

    if (canResetPassword) {
      additionalActions.push({
        key: 'reset-password',
        iconName: faKey,
        type: 'primary',
        title: t('Reset password'),
        onClick: s => setSelectedStudent({ data: s, type: RESET_PASSWORD }),
        disabledCallback: s => s.user?.isExternal,
        titleCallback: s => s.user?.isExternal ? t('This user is external and cannot have its password reset') : ''
      })
    }

    return additionalActions
  }, [usePublipostage, canResetPassword, t, setSelectedStudent])

  const resetSelectedStudent = useCallback(() => setSelectedStudent({ data: null, type: null }), [])
  const localStudents = useMemo(() => institutionStudents.data.map(s => {
    let section = null

    if (s.sections.length > 0) {
      if (s.sections[0].sectionToYear) {
        section = s.sections[0].sectionToYear.sectionAbbreviation
      } else if (s.sections[0].abbreviation) {
        section = s.sections[0].abbreviation
      } else if (s.sections[0].comment) {
        section = s.sections[0].comment
      }
    }

    return { ...s, section, status: getStatus(s) }
  }), [institutionStudents.data, t])

  const filteredStudents = useMemo(() => {
    if (selectedStatuses.length > 0) {
      return localStudents.filter(student => selectedStatuses.includes(student.status))
    }

    return localStudents
  }, [localStudents, selectedStatuses])

  const statusOptions = useMemo(() => StudentStatusLabels.map(status => ({ value: status, label: t('student_status_codes.' + status.toLowerCase()) })), [t])

  const noDataText = useMemo(() => {
    if (localStudents.length === 0) {
      return t('Your institution currently has no student.')
    }

    return t('No data')
  }, [localStudents.length, t])

  const statusFilterButton = useMemo(() => (
    <Dropdown
      trigger={['click']}
      overlay={(
        <Menu>
          <Menu.ItemGroup title={t('misc.filters')}>
            <Menu.Item disabled style={{ cursor: 'default' }}>
              <ComponentWithIconLabel icon={faUserGraduate} label={t('common.status')}>
                <Select
                  mode='multiple'
                  style={{ width: '280px' }}
                  value={selectedStatuses}
                  onChange={setSelectedStatuses}
                >
                  {statusOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}> {option.label} </Select.Option>
                  ))}
                </Select>
              </ComponentWithIconLabel>
            </Menu.Item>
          </Menu.ItemGroup>
        </Menu>
      )}
    >
      <Button>
        <FontAwesomeIcon icon={faFilter} />
      </Button>
    </Dropdown>
  ), [selectedStatuses, statusOptions, t])

  return (
    <div>
      <SmartTable
        columns={columns}
        data={filteredStudents}
        loading={institutionStudents.loading}
        noDataText={noDataText}
        keepTableDisplayedWhenNoData
        leftActionsExtra={statusFilterButton}
        onDataDelete={undefined}
        noDataDelete
        additionalActions={additionalActions}
      />
      <StudentInfo
        student={selectedStudent.type === STUDENT_INFO ? selectedStudent.data : null}
        onClose={resetSelectedStudent}
      />
      {canResetPassword && (
        <AdminResetPassword
          userToResetPassword={selectedStudent.type === RESET_PASSWORD ? selectedStudent.data : null}
          onClose={resetSelectedStudent}
        />
      )}
      {usePublipostage && (
        <PublipostageDrawer
          student={selectedStudent.type === InstitutionOptions.PUBLIPOSTAGE ? selectedStudent.data : null}
          onClose={resetSelectedStudent}
        />
      )}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(StudentsView)
